import { create, ApisauceInstance, ApiErrorResponse } from 'apisauce';
import Reactotron from 'reactotron-react-js';

import { urls, useReactotron } from '../config/configConstants';
import { initApi } from 'shovel-lib';
import { isStudentChange, writeAnswered, writeSent } from '../utils/serverChanges';
import { onOtherRequestFailed, onRequestAnswered, onRequestFailed } from '../utils/maintenance';

const AUTHORIZATION_HEADER_NAME = 'Authorization';
/**
 * Network instance that wraps the request/response interaction with our backend.
 *
 * Important flow to understand:
 * When user logs into the application, the authorization header will be set via {@link setAccessToken} method.
 * Every following request will hold this header until either the access token expires or user logs out.
 * - When access token expires, the {@link refreshToken} operation will again set the header.
 *
 * This may look odd in situation where if multiple requests are executed concurrently, one may think that
 * if token expires in between, all requests will reuse the expired access token and fail,
 * however the responsibility of operations {@link refreshToken} and {@link callApi} will be
 * to synchronize the refreshing of the access token, calling the {@link setAccessToken} only once
 * and re-trying the requests afterwards.
 **/
export const network: ApisauceInstance = create({
  baseURL: urls.backend
});

export const eventsNetwork: ApisauceInstance = create({
  baseURL: urls.eventService
});
export const lambdaNetwork: ApisauceInstance = create({
  baseURL: urls.lambda
});
export const shovelAiNetwork: ApisauceInstance = create({
  baseURL: urls.shovelAi
});
/**
 * Reports to Shovel's activity log (api/activityLogApi.ts). Its own instance on purpose: no maintenance redirect (an ad
 * blocker or a hiccup must never send the student to the maintenance page) and not counted as a change Undo waits for
 * (a report is not a change the student made).
 */
export const actionsNetwork: ApisauceInstance = create({
  baseURL: urls.backend,
  timeout: 5000
});

// The activity log tells the web app's changes from the phone's and AI apps' by this. Backend only: the event-service
// and the AI service read it differently. The phone app shows some of these pages (the calendar, the sidebar) inside
// itself and says so in `deviceType` before they load (utils/screenUtils.ts isMobileApp); what happens there is the
// phone's.
const insidePhoneApp = (() => {
  try {
    return !!localStorage.getItem('deviceType');
  } catch {
    return false;
  }
})();
export const clientName = insidePhoneApp ? 'mobile' : 'web';
network.setHeader('X-Shovel-Client', clientName);
actionsNetwork.setHeader('X-Shovel-Client', clientName);

export const maintenanceCodes = [null, 502, 503, 504];

/**
 * No answer, or a 502, 503, 504: the maintenance page, but only once a check finds Shovel itself down
 * (utils/maintenance.ts). Usually it's the student's internet for a few seconds. Any other answer means the internet
 * is there.
 */
network.addMonitor(response => {
  if (maintenanceCodes.includes(response.status ?? null)) {
    onRequestFailed(!!response.config && isStudentChange(response.config));
  } else {
    onRequestAnswered();
  }
});

// The other services' no-answers: the offline bar speaks for them too (utils/maintenance.ts), never the maintenance page.
[eventsNetwork, lambdaNetwork, shovelAiNetwork].forEach(instance =>
  instance.addMonitor(response => {
    if (response.status) return;
    onOtherRequestFailed(!!response.config && isStudentChange(response.config), instance === eventsNetwork);
  })
);

// The student's changes on their way, which Cmd+Z waits for (utils/serverChanges.ts).
[network, eventsNetwork, lambdaNetwork, shovelAiNetwork].forEach(instance => {
  instance.addRequestTransform(writeSent);
  instance.addMonitor(response => writeAnswered(response.config));
});

export const setAccessToken = (accessToken: string) => {
  network.setHeader(AUTHORIZATION_HEADER_NAME, `Bearer ${accessToken}`);
  eventsNetwork.setHeader(AUTHORIZATION_HEADER_NAME, `Bearer ${accessToken}`);
  lambdaNetwork.setHeader(AUTHORIZATION_HEADER_NAME, `Bearer ${accessToken}`);
  shovelAiNetwork.setHeader(AUTHORIZATION_HEADER_NAME, `Bearer ${accessToken}`);
  actionsNetwork.setHeader(AUTHORIZATION_HEADER_NAME, `Bearer ${accessToken}`);
};

export const removeAccessToken = () => {
  network.deleteHeader(AUTHORIZATION_HEADER_NAME);
  eventsNetwork.deleteHeader(AUTHORIZATION_HEADER_NAME);
  lambdaNetwork.deleteHeader(AUTHORIZATION_HEADER_NAME);
  shovelAiNetwork.deleteHeader(AUTHORIZATION_HEADER_NAME);
  actionsNetwork.deleteHeader(AUTHORIZATION_HEADER_NAME);
};

/**
 * helper method to prevent app crashing when backend is down (response.data is null in that case)
 */
export const getErrorMessage = (response: ApiErrorResponse<any>) => response.data && response.data.message;

export const getErrorCode = (response: ApiErrorResponse<any>) =>
  response.data && response.data.errorCodes && response.data.errorCodes[0];

if (useReactotron) {
  // @ts-ignore
  network.addMonitor(Reactotron.apisauce);
  // @ts-ignore
  eventsNetwork.addMonitor(Reactotron.apisauce);
  // @ts-ignore
  lambdaNetwork.addMonitor(Reactotron.apisauce);
  // @ts-ignore
  shovelAiNetwork.addMonitor(Reactotron.apisauce);
}

initApi(network, eventsNetwork);
